import { useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconQuestionMarkFilled } from '@iwoca/icons';
import { Button, CheckBox, Table, Tooltip } from '@iwoca/orion';
import { ColumnDef } from '@iwoca/orion/lib/components/table/Table';
import { clsx } from 'clsx';

import {
  PackageLinkView,
  PackageLinkViewSwitcher,
} from './components/PackageLinkViewSwitcher/PackageLinkViewSwitcher';
import {
  normalisePackageLinkTableData,
  PackageLinkTableRow,
} from './utils/normalisePackageLinkTableData';
import {
  useGetSellerByHandle,
  usePutIwocapayPayLinkPackage,
} from '../../../../api/lending/lapiHooks';
import { PayMeLink } from '../../../../components/PayMeLink/PayMeLink';
import { createToast } from '../../../../store/IwToast';
import { usePackageLinks } from '../usePackageLinks';

const ValueWithTooltip = ({
  value,
  tooltipConfig,
}: {
  value: string;
  tooltipConfig: { showTooltip: boolean; text: string };
}) => (
  <div className="flex items-center gap-l">
    <span className={tooltipConfig.showTooltip ? 'text-structure-60' : ''}>
      {value}
    </span>
    {tooltipConfig.showTooltip && (
      <div className="flex h-[20px] w-[20px] items-center rounded-l border border-primary-40">
        <Tooltip
          className="text-primary-10"
          content={tooltipConfig.text}
          placement="bottom"
        >
          <IconQuestionMarkFilled fill="primary-10" height="18" width="18" />
        </Tooltip>
      </div>
    )}
  </div>
);

const CUSTOMER_INPUT_FALLBACK = 'Customer input';

const handleTooltip = (value: string) => {
  if (value === CUSTOMER_INPUT_FALLBACK) {
    return {
      showTooltip: true,
      text: 'Your customer will put in this detail on their end',
    };
  }
  return { showTooltip: false, text: '' };
};

export const PackageLinkTable = ({
  sellerHandle,
}: {
  sellerHandle: string;
}) => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [currentView, setCurrentView] = useState<PackageLinkView>('ACTIVE');
  const { seller } = useGetSellerByHandle(sellerHandle);

  const { packageLinks } = usePackageLinks({ includeDeleted: true });
  const { putIwocapayPayLinkPackage, isPending } =
    usePutIwocapayPayLinkPackage();

  const activeLinks = packageLinks.filter((pl) => !pl.deleted_at);
  const archivedLinks = packageLinks.filter((pl) => pl.deleted_at);
  const displayedLinks = currentView === 'ACTIVE' ? activeLinks : archivedLinks;

  const trackPackageLinkArchived = (view: PackageLinkView) => {
    tracking.ampli.packageLinkArchived({
      seller_id: seller?.seller_id,
      archived: view === 'ACTIVE',
    });
  };

  const trackArchivedTableViewed = (view: PackageLinkView) => {
    if (view === 'ARCHIVED') {
      tracking.ampli.archivedPackageLinkTableViewed({
        seller_id: seller?.seller_id,
      });
    }
  };

  const handleAddSelectedRow = (packageIds: string[]) => {
    const newRows = [...new Set([...selectedRows, ...packageIds])];
    setSelectedRows(newRows);
  };

  const handleRemoveSelectedRow = (packageIds: string[]) => {
    const newRows = selectedRows.filter(
      (packageId) => packageIds.indexOf(packageId) < 0,
    );
    setSelectedRows(newRows);
  };

  const handleArchive = async () => {
    try {
      await putIwocapayPayLinkPackage({
        archive: currentView === 'ACTIVE',
        package_ids: selectedRows,
      });
      createToast(
        <>
          <b>Success</b> - Package link{selectedRows.length !== 1 ? 's' : ''}{' '}
          {currentView === 'ACTIVE' ? 'archived' : 'unarchived'}
        </>,
      );
      setSelectedRows([]);
      trackPackageLinkArchived(currentView);
    } catch {
      createToast(
        <>
          <b>Error</b> - Failed to{' '}
          {currentView === 'ACTIVE' ? 'archive' : 'unarchive'} package link
          {selectedRows.length !== 1 ? 's' : ''}
        </>,
        { variant: 'failure' },
      );
    }
  };

  const tableData: PackageLinkTableRow[] = normalisePackageLinkTableData({
    packageLinks: displayedLinks,
    sellerHandle,
    selectedRows,
    handleAddSelectedRow,
    handleRemoveSelectedRow,
  });

  const columns: ColumnDef<PackageLinkTableRow>[] = [
    {
      id: 'checkboxRow',
      header: (info) => (
        <CheckBox
          checked={
            info.table.getRowModel().rows.length > 0 &&
            info.table.getRowModel().rows.length ===
              info.table.getRowModel().rows[0].original.selectedRows.length
          }
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const allRows = info.table.getRowModel().rows;
            const isChecked = event.target.checked;

            if (isChecked) {
              const allPackageIds = allRows.map(
                (row) => row.original.package_id,
              );
              allRows[0].original.handleAddSelectedRow(allPackageIds);
            } else {
              const allPackageIds = allRows.map(
                (row) => row.original.package_id,
              );
              allRows[0].original.handleRemoveSelectedRow(allPackageIds);
            }
          }}
        />
      ),
      cell: (info) => (
        <CheckBox
          checked={info.row.original.selectedRows.includes(
            info.row.original.package_id,
          )}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const checked = event.target.checked;
            if (checked) {
              info.row.original.handleAddSelectedRow([
                info.row.original.package_id,
              ]);
            } else {
              info.row.original.handleRemoveSelectedRow([
                info.row.original.package_id,
              ]);
            }
          }}
        />
      ),
      className: 'w-[10%]',
    },
    {
      accessorKey: 'name',
      header: 'Name',
      className: 'w-[50%]',
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: (info) => (
        <ValueWithTooltip
          value={info.row.original.amount}
          tooltipConfig={handleTooltip(info.row.original.amount)}
        />
      ),
      className: 'w-[30%]',
    },
    {
      accessorKey: 'reference',
      header: 'Reference',
      cell: (info) => (
        <ValueWithTooltip
          value={info.row.original.reference}
          tooltipConfig={handleTooltip(info.row.original.reference)}
        />
      ),
      className: 'w-[40%]',
    },
    {
      accessorKey: 'dateCreated',
      header: 'Date Created',
      className: 'w-[30%]',
    },
    {
      id: 'payLinkUrl',
      header: 'Your Package Link',
      cell: (info) => (
        <PayMeLink payMeUrl={info.row.original.payLinkUrl} iconOnly={true} />
      ),
      className: 'w-full [&_div]:w-full',
    },
  ];

  return (
    <div className={clsx({ 'pb-[120px]': selectedRows.length > 0 }, 'pb-l')}>
      <PackageLinkViewSwitcher
        currentView={currentView}
        onChange={(view) => {
          setCurrentView(view);
          setSelectedRows([]);
          trackArchivedTableViewed(view);
        }}
      />

      {tableData.length === 0 ? (
        <div className="py-xl text-center text-structure-60">
          {currentView === 'ACTIVE'
            ? 'No package links found'
            : 'No archived package links found'}
        </div>
      ) : (
        <Table
          data={tableData}
          columns={columns}
          loading={false}
          className="[&_td]:!items-center [&_td]:px-l [&_td]:py-r"
        />
      )}

      {selectedRows.length > 0 && (
        <div className="fixed bottom-0 left-[200px] z-10 flex w-[calc(100%-200px)] items-center justify-center gap-xl bg-secondary-90 p-xl shadow-black-l m:left-0 m:w-full">
          <span className="text-black">
            {selectedRows.length} item{selectedRows.length !== 1 ? 's' : ''}{' '}
            selected
          </span>
          <Button
            variant="tertiary"
            onClick={() => setSelectedRows([])}
            className="justify-self-center"
          >
            Deselect all
          </Button>
          <Button onClick={handleArchive} disabled={isPending}>
            {isPending
              ? currentView === 'ACTIVE'
                ? 'Archiving...'
                : 'Unarchiving...'
              : currentView === 'ACTIVE'
                ? 'Archive'
                : 'Unarchive'}
          </Button>
        </div>
      )}
    </div>
  );
};
